export interface PrinterStatus {
  connected: boolean;
  type: 'serial' | 'bluetooth' | 'browser' | 'none';
  deviceName?: string;
}

class PrinterService {
  private port: any | null = null;
  private writer: any | null = null;
  private status: PrinterStatus = {
    connected: false,
    type: 'none',
  };

  public getStatus(): PrinterStatus {
    return { ...this.status };
  }

  public async tryAutoReconnectSerial(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !(navigator as any).serial) {
      return false;
    }
    try {
      const ports = await (navigator as any).serial.getPorts();
      if (ports && ports.length > 0) {
        this.port = ports[0];
        await this.port.open({ baudRate: 9600 });
        this.writer = this.port.writable?.getWriter();
        this.status = {
          connected: true,
          type: 'serial',
          deviceName: 'USB/Serial Thermal Printer',
        };
        return true;
      }
    } catch (e) {
      console.warn('Auto reconnect serial printer failed:', e);
    }
    return false;
  }

  public async connectSerial(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !(navigator as any).serial) {
      throw new Error('Web Serial API is not supported in this browser. Please use Chrome or Edge.');
    }
    try {
      if (this.port) {
        await this.disconnect();
      }
      this.port = await (navigator as any).serial.requestPort();
      await this.port.open({ baudRate: 9600 });
      this.writer = this.port.writable?.getWriter();
      this.status = {
        connected: true,
        type: 'serial',
        deviceName: 'USB/Serial Thermal Printer',
      };
      return true;
    } catch (e) {
      console.error('Serial connection error:', e);
      throw e;
    }
  }

  public async connectBluetooth(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !(navigator as any).bluetooth) {
      throw new Error('Web Bluetooth API is not supported in this browser.');
    }
    try {
      const device = await (navigator as any).bluetooth.requestDevice({
        filters: [{ services: ['000018f0-0000-1000-8000-00805f9b34fb'] }],
        optionalServices: ['000018f0-0000-1000-8000-00805f9b34fb'],
      });
      await device.gatt.connect();
      this.status = {
        connected: true,
        type: 'bluetooth',
        deviceName: device.name || 'Bluetooth POS Printer',
      };
      return true;
    } catch (e) {
      console.error('Bluetooth connection error:', e);
      throw e;
    }
  }

  public async disconnect(): Promise<void> {
    try {
      if (this.writer) {
        this.writer.releaseLock();
        this.writer = null;
      }
      if (this.port) {
        await this.port.close();
        this.port = null;
      }
    } catch (e) {
      console.warn('Error closing printer port:', e);
    } finally {
      this.status = {
        connected: false,
        type: 'none',
      };
    }
  }

  public async kickDrawer(): Promise<void> {
    // ESC p 0 50 250 (pulse pin 2 for cash drawer kick-out)
    const kickCommand = new Uint8Array([0x1b, 0x70, 0x00, 0x32, 0xfa]);
    if (this.writer) {
      await this.writer.write(kickCommand);
    } else {
      console.info('Simulating cash drawer kick-out pulse (no direct serial printer connected).');
    }
  }

  public async printRaw(data: Uint8Array): Promise<void> {
    if (this.writer) {
      await this.writer.write(data);
    } else {
      // Fallback to window browser print if no direct hardware connection
      window.print();
    }
  }
}

export const printerService = new PrinterService();
